import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import patientBlisterMedicationsService from '../../../../../services/patientBlisterMedicationsService';
import { subscriptionService } from '../../../../../services/subscriptionService';
import {
  patientBlisterMedicationsQueryKey,
  patientBlisterMedicationsQueryOptions,
} from '../../../../../hooks/usePatientBlisterMedications';
import { patientBlisterQueryKey } from '../../../../../hooks/usePatientBlister';
import type { ByRequestOnlyPlan, RxBasketLine, RxBasketMembership } from './rxRowActions';

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/**
 * PRX-137 — which of the patient's prescriptions sit on a subscription basket,
 * and whether each is "by request only" (`isPrn`).
 *
 * The source is the existing per-drug basket read
 * (`GET /api/patients/:pharmacyId/:patientId/blister/medications`,
 * `viewPatients`). On the server it reads every live basket line for any
 * patient: it reports `blisterPack` and does not hide rows behind it. It is
 * read under the Blister pack grid's own query key, so a write in either
 * place repaints both. The Blister pack grid keeps its own rule: it asks only
 * for a flagged patient. This observer is the Prescriptions tab's.
 *
 * The match is by exact Rx number. A failed read is `error`, never "no
 * basket". A 404 (the patient has no PHOX record at this pharmacy, so there is
 * no basket to be on) reads as no lines. It is derived here and never written
 * into the shared cache.
 */
export function useRxBasketLines(pharmacyId: string | undefined, patientId: string | undefined, enabled: boolean) {
  const query = useQuery({
    ...patientBlisterMedicationsQueryOptions(pharmacyId, patientId),
    enabled: enabled && !!pharmacyId && !!patientId,
  });

  const linesByRx = useMemo(() => {
    const map = new Map<string, RxBasketLine[]>();
    for (const row of query.data?.rows ?? []) {
      const key = String(row.rxNumber);
      const lines = map.get(key) ?? [];
      lines.push({
        billingItemId: row.billingItemId,
        medicationIndex: row.medicationIndex,
        rxNumber: key,
        isPrn: row.isPrn === true,
        hasBlisterSettings: row.blister != null,
      });
      map.set(key, lines);
    }
    return map;
  }, [query.data]);

  const noPhoxRecord = query.isError && statusOf(query.error) === 404;
  const blisterPack = query.data?.blisterPack === true;

  const membershipFor = useCallback(
    (rxNumber: string | number): RxBasketMembership => {
      if (noPhoxRecord) return { status: 'ready', blisterPack: false, lines: [] };
      if (query.isError) return { status: 'error' };
      if (!query.data) return { status: 'loading' };
      return { status: 'ready', blisterPack, lines: linesByRx.get(String(rxNumber)) ?? [] };
    },
    [noPhoxRecord, query.isError, query.data, blisterPack, linesByRx],
  );

  return { membershipFor };
}

/**
 * Write "by request only" for one basket line through the route its plan
 * names (see `byRequestOnlyPlan`). Never a PMS write.
 */
export function useSetRxByRequestOnly(pharmacyId: string | undefined, patientId: string | undefined) {
  const queryClient = useQueryClient();
  const key = patientBlisterMedicationsQueryKey(pharmacyId, patientId);

  return useMutation({
    mutationFn: async (plan: Exclude<ByRequestOnlyPlan, { kind: 'disabled' }>) => {
      if (plan.kind === 'blisterAskFirst') {
        const data = await patientBlisterMedicationsService.update(
          pharmacyId as string,
          patientId as string,
          plan.line.rxNumber,
          { send: 'ask_first', billingItemId: plan.line.billingItemId, medicationIndex: plan.line.medicationIndex },
        );
        return { plan, data };
      }
      await subscriptionService.setMedicationPrn(plan.line.billingItemId, plan.line.rxNumber, plan.next);
      return { plan, data: null };
    },
    onSuccess: ({ data }) => {
      if (data) {
        // The blister route answers the whole refreshed grid.
        queryClient.setQueryData(key, data);
        queryClient.invalidateQueries({ queryKey: patientBlisterQueryKey(pharmacyId, patientId) });
      } else {
        queryClient.invalidateQueries({ queryKey: key });
      }
      // Either route recomputes the basket's next follow-up date, so the
      // patient's subscriptions (Patient 360 reads them as billing items) and
      // the billing queue are read again.
      queryClient.invalidateQueries({ queryKey: ['patient', 'billing'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}
